import { makeAutoObservable, observable, runInAction } from "mobx";
import { InfiniteQueryObserver, isCancelledError, type InfiniteData } from "@tanstack/react-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import { QUERY_CACHE_FLAG } from "@/lib/launchdarkly/flags";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { FRESHNESS } from "@/queries/policies";
import { scopeFor, ScopeNotReadyError, type QueryScope } from "@/queries/scope";
import {
  memberTeamsQuery,
  teamChildrenQuery,
  teamDetailQuery,
  teamMembersQuery,
  teamsBankKey,
  teamsBankQuery,
  teamsIncludeOf,
  teamsPageQuery,
  type TeamsInclude,
} from "@/queries/teams";
import type { RootStore } from "./root.store";
import type {
  TCreateTeam,
  TDeleteTeamOptions,
  TMemberTeam,
  TMemberTeamChange,
  TTeam,
  TTeamMember,
  TTeamDeletePreflight,
  TTeamPermissionGraph,
  TTeamsListQuery,
  TTeamsPage,
  TUpdateTeam,
} from "@/types/team";

function sortTeams(teams: TTeam[] | undefined | null): TTeam[] {
  if (!Array.isArray(teams)) return [];
  return [...teams].sort((a, b) => a.name.localeCompare(b.name));
}

type TChildrenMode = "direct" | "all";

// The children cache is keyed by mode as well as team id: the sub-teams tab
// caches the "direct" children while the add-members dialog caches the "all"
// descendants for the same team, and a bare team-id key would let the two
// clobber each other.
function childrenCacheKey(teamId: string, mode: TChildrenMode): string {
  return `${teamId}::${mode}`;
}

type TFetchTeamsOptions = { includeAll?: boolean; includeMembers?: boolean };

/** Every row of a page set, in order, once each. */
function pagesToRows(pages: readonly TTeamsPage[]): TTeam[] {
  const seen = new Set<string>();
  const rows: TTeam[] = [];
  for (const page of pages) {
    for (const team of page.results) {
      if (seen.has(team.id)) continue;
      seen.add(team.id);
      rows.push(team);
    }
  }
  return rows;
}

function listedKey(slug: string, include: TeamsInclude): string {
  return `${slug}::${include}`;
}

/** The mode whose rows an `include=…members` list also answers for. */
const WITHOUT_ROSTERS: Partial<Record<TeamsInclude, TeamsInclude>> = {
  members: "none",
  "all+members": "all",
};

export class WorkspaceTeamsStore {
  teamsBySlug = observable.map<string, TTeam[]>();
  membersByTeamId = observable.map<string, TTeamMember[]>();
  childrenByTeamId = observable.map<string, TTeam[]>();
  fetchedSlugs = observable.map<string, boolean>();
  // With the `query-cache` flag on (NG-2545): which teams each include mode's
  // list holds, keyed `slug::include`. `teamsBySlug` merges every mode into one
  // array (the NG-439 bridge, kept while the flag exists), so `teamsFor` reads
  // a mode's own rows back out of it, and a plain reader is never shown a team
  // only the `include=all` list carries (NG-2413).
  listedIdsByMode = observable.map<string, ReadonlySet<string>>();
  // Per-workspace "can the caller create a team at all?" verdict, backing the
  // "Create team" button gate for callers without a workspace-scope team.create
  // grant (NG-2176). `undefined` = not yet answered (the button stays hidden to
  // avoid a flash); a boolean is the cached probe result. Same shape as
  // `ProgramsStore.canCreateByWorkspace` (#3206).
  canCreateByWorkspace = observable.map<string, boolean>();
  isFetching = false;
  // Last teams-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)fetch starts; set when fetchTeams rejects.
  loadError: Error | null = null;
  searchQuery = "";

  // --- Server-driven teams-list page (GH-3993) --------------------------------
  // Deliberately a SEPARATE cache from `teamsBySlug`: the teams page searches,
  // hierarchy-orders and paginates server-side, so its rows are a filtered,
  // partial, non-alphabetical slice. Writing that into the shared cache would
  // corrupt what the sidebar and every team picker read.
  pagedTeams: TTeam[] = [];
  listPageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  isListPageLoading = false;
  isListLoadingMore = false;
  listError: Error | null = null;
  private lastListQuery: TTeamsListQuery = {};

  private rootStore: RootStore;
  // In-flight `fetchTeams` promises keyed by fetch key. RR7 runs the parent
  // teams layout loader and the child index loader in PARALLEL, so both call
  // `fetchTeams(slug)` on a cold load — sharing the promise collapses that into
  // a single network request instead of racing two. Non-observable.
  private pendingFetches = new Map<string, Promise<TTeam[]>>();
  // Slugs whose `teamsBySlug` entry was last written by an `includeAll` fetch,
  // i.e. holds the whole workspace rather than the caller's own teams. Read by
  // `mergeIntoCache` so a later viewer-scoped fetch cannot NARROW it. Every
  // consumer reads `teamsBySlug.get(slug)`, including the `includeAll` ones, so
  // the cache stays keyed by slug and the mode is tracked beside it rather than
  // folded into the key. Non-observable: it describes the cache, and a reaction
  // on it would fire for a write that changed no team.
  private wideSlugs = new Set<string>();
  // Counts this store's own team writes (create, update, delete), and records the
  // count at each team's latest one, so a read can tell which teams changed while
  // it was in flight (NG-2295). A list read answered on the server before a save,
  // but reaching the browser after it, would otherwise put the pre-save row back:
  // a rename reverted on screen with Save enabled again, because ParentTeamField
  // revalidates the list on every General-tab mount. Non-observable: bookkeeping
  // about the cache, not something a view renders.
  private writeCount = 0;
  private lastWriteByTeamId = new Map<string, number>();
  // The same bookkeeping for each team's roster (NG-2311). The team page's loader
  // reads the roster and the Members tab reads it again on mount, and the table
  // keeps its rows on screen while that second read is in flight. A member removed
  // in that window came back when the older read landed, and stayed, because
  // nothing reads the roster again. Kept apart from the team-row counts so a
  // roster write never makes a list read drop that team's fresher row.
  private rosterWriteCount = 0;
  private lastRosterWriteByTeamId = new Map<string, number>();
  // With the `query-cache` flag on (NG-2545): counts the writes that dropped
  // cached team lists, so a list read the drop cancelled knows to read again,
  // and records, per cached list, the answer last put into `teamsBySlug`, so a
  // cache hit is not projected over writes applied since. Non-observable.
  private bankDropCount = 0;
  private projectedBankAt = new Map<string, number>();
  // The same for the teams page (R15): which cached page set, at which answer,
  // `pagedTeams` was last filled from.
  private projectedPage: { key: string; at: number } | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        pendingFetches: false,
        lastListQuery: false,
        wideSlugs: false,
        writeCount: false,
        lastWriteByTeamId: false,
        rosterWriteCount: false,
        lastRosterWriteByTeamId: false,
        bankDropCount: false,
        projectedBankAt: false,
        projectedPage: false,
      } as never,
      { autoBind: true },
    );
  }

  get teams(): TTeam[] {
    const slug = this.rootStore.router.workspaceSlug;
    return slug ? (this.teamsBySlug.get(slug) ?? []) : [];
  }

  /**
   * The teams one include mode lists in `slug`: what a reader that loaded the
   * list with these options should render. With the `query-cache` flag off this
   * is `teamsBySlug.get(slug)`, unchanged. With it on, it is that mode's own
   * rows, so a reader is never handed teams another mode's list brought in, and
   * an empty array until the mode has loaded.
   */
  teamsFor(slug: string, options?: TFetchTeamsOptions): TTeam[] {
    const merged = this.teamsBySlug.get(slug) ?? [];
    if (!this.queryCacheEnabled) return merged;
    const listed = this.listedIdsByMode.get(listedKey(slug, teamsIncludeOf(options)));
    if (!listed) return [];
    return merged.filter((team) => listed.has(team.id));
  }

  /**
   * Whether a team picker should load the list only when it opens, rather than
   * when it mounts (NG-2545). True with the `query-cache` flag on; with it off,
   * pickers keep loading on mount as they always have.
   */
  get loadsOnDemand(): boolean {
    return this.queryCacheEnabled;
  }

  /** `teamsFor` in the workspace the router is on. */
  teamsInView(options?: TFetchTeamsOptions): TTeam[] {
    const slug = this.rootStore.router.workspaceSlug;
    return slug ? this.teamsFor(slug, options) : [];
  }

  get filteredTeams(): TTeam[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.teams;
    return this.teams.filter((t) => t.name.toLowerCase().includes(q));
  }

  getTeamById(id: string): TTeam | undefined {
    return this.teams.find((t) => t.id === id);
  }

  getMembers(teamId: string): TTeamMember[] {
    return this.membersByTeamId.get(teamId) ?? [];
  }

  getChildren(teamId: string, mode: TChildrenMode = "direct"): TTeam[] {
    return this.childrenByTeamId.get(childrenCacheKey(teamId, mode)) ?? [];
  }

  hasFetchedForSlug(slug: string, options?: TFetchTeamsOptions): boolean {
    return Boolean(this.fetchedSlugs.get(this.fetchKey(slug, options)));
  }

  setSearchQuery(q: string): void {
    this.searchQuery = q;
  }

  private fetchKey(slug: string, options: TFetchTeamsOptions | undefined): string {
    let key = slug;
    if (options?.includeAll) key += "::all";
    if (options?.includeMembers) key += "::members";
    return key;
  }

  fetchTeams(slug: string, options?: TFetchTeamsOptions): Promise<TTeam[]> {
    const key = this.fetchKey(slug, options);
    // A members fetch returns a superset of the plain list, so a plain request
    // can piggyback on an in-flight `include=members` one — the parent teams
    // layout loader (plain) and the index loader (members) run in parallel.
    const pending =
      this.pendingFetches.get(key) ??
      (!options?.includeAll && !options?.includeMembers
        ? this.pendingFetches.get(this.fetchKey(slug, { includeMembers: true }))
        : undefined);
    if (pending) return pending;
    const promise = this.loadTeams(slug, key, options);
    this.pendingFetches.set(key, promise);
    return promise;
  }

  /**
   * Fire-and-forget cold-cache prime for list columns / pickers. `loadTeams`
   * rethrows, so a bare `void fetchTeams(...)` lets the rejection escape to
   * `onunhandledrejection` — e.g. a cold-load auth-ready race where the request
   * fires before the session is attached and the API returns 403 (GH-3483), or
   * an expected permission boundary. Self-catch + `console.warn` so the caller
   * can't crash the page, while the failure still reaches Sentry Logs via
   * `consoleLoggingIntegration` (mirrors `WorkspaceMembersStore.ensureMembers`).
   */
  async ensureTeams(slug: string, options?: TFetchTeamsOptions): Promise<void> {
    // Cache-first, which is what every caller already assumed and what the name
    // says. `fetchTeams` dedupes only IN-FLIGHT promises, so before NG-439 a
    // second `ensureTeams` for a slug the store had already fetched — and
    // already marked fetched — issued a fresh unpaginated list GET. These
    // surfaces remount on every navigation, so list -> detail -> back was three
    // full fetches of data nobody had invalidated. `ensureTeamMembers` had this
    // right and is now the general rule.
    //
    // A caller that genuinely wants to revalidate calls `fetchTeams` directly:
    // that is the explicit "fetch now" API and still bypasses this guard.
    if (this.hasFetchedForSlug(slug, options) && this.cachedListIsCurrent(slug, options)) return;
    try {
      await this.fetchTeams(slug, options);
    } catch (err) {
      console.warn(`[workspace-teams] ensureTeams failed for workspace ${slug}`, err);
    }
  }

  /**
   * Cache-first prime of every team's membership for the workspace, for
   * surfaces that render a person's teams without ever visiting a teams page —
   * the capacity member panel (GH-4132), which used to depend on some other
   * page having warmed `membersByTeamId` earlier in the session.
   *
   * ONE request: `include=members` embeds each team's members in the list
   * payload (#2016), so this never fans out to a per-team `fetchMembers` loop
   * (a workspace can have dozens of teams). A second call is a no-op once the
   * members key is marked fetched. Self-catching via `ensureTeams`, so a
   * failure only drops the team names from the surface that asked.
   */
  async ensureTeamMembers(slug: string): Promise<void> {
    // The short-circuit it used to carry now lives in `ensureTeams`, for every
    // mode rather than this one.
    await this.ensureTeams(slug, { includeMembers: true });
  }

  /**
   * What a finished fetch should leave in `teamsBySlug`.
   *
   * The cache is keyed by SLUG, but the fetch key distinguishes include modes,
   * so before NG-439 an `includeAll` prime and any viewer-scoped prime simply
   * overwrote each other's array. A later plain fetch could therefore NARROW a
   * cache that a consumer had primed with `includeAll` and still needed the
   * superset of — `intake-submissions-list` and `plan-projects-gantt` both
   * prime `includeAll` and then read `teamsBySlug.get(slug)`.
   *
   * So a narrower result is merged into a wider cache instead of replacing it:
   * the incoming copy of a team wins (it is fresher), and teams only the
   * superset knows about survive. An `includeAll` result replaces outright —
   * it IS the authoritative whole-workspace list.
   */
  private mergeIntoCache(slug: string, incoming: TTeam[], options?: TFetchTeamsOptions): TTeam[] {
    if (options?.includeAll) {
      this.wideSlugs.add(slug);
      return sortTeams(incoming);
    }
    if (!this.wideSlugs.has(slug)) return sortTeams(incoming);
    const byId = new Map((this.teamsBySlug.get(slug) ?? []).map((team) => [team.id, team]));
    for (const team of incoming) byId.set(team.id, team);
    return sortTeams([...byId.values()]);
  }

  /** Record a team write this store has just applied (NG-2295). */
  private noteWrite(teamId: string): void {
    this.writeCount += 1;
    this.lastWriteByTeamId.set(teamId, this.writeCount);
  }

  /** Whether this store wrote `teamId` after a read that started at `readStartedAt`. */
  private writtenSince(teamId: string, readStartedAt: number): boolean {
    return (this.lastWriteByTeamId.get(teamId) ?? 0) > readStartedAt;
  }

  /** Record a roster write this store has just applied (NG-2311). */
  private noteRosterWrite(teamId: string): void {
    this.rosterWriteCount += 1;
    this.lastRosterWriteByTeamId.set(teamId, this.rosterWriteCount);
  }

  /** Whether this store wrote `teamId`'s roster after a read that started at `readStartedAt`. */
  private rosterWrittenSince(teamId: string, readStartedAt: number): boolean {
    return (this.lastRosterWriteByTeamId.get(teamId) ?? 0) > readStartedAt;
  }

  /**
   * `incoming`, with every team this store wrote while the read was in flight
   * left the way the write left it: the saved copy after a create or an update,
   * and absent after a delete. The server answered the read before those writes,
   * so its copy of them is older than the one already cached (NG-2295).
   */
  private withoutStaleRows(slug: string, incoming: TTeam[], readStartedAt: number): TTeam[] {
    if (this.writeCount === readStartedAt) return incoming;
    const rows = incoming.filter((team) => !this.writtenSince(team.id, readStartedAt));
    for (const team of this.teamsBySlug.get(slug) ?? []) {
      if (this.writtenSince(team.id, readStartedAt)) rows.push(team);
    }
    return rows;
  }

  private get queryCacheEnabled(): boolean {
    return this.rootStore.featureFlags.isEnabled(QUERY_CACHE_FLAG);
  }

  /**
   * Whether `ensureTeams` may skip a read it has made before. With the
   * `query-cache` flag off, always: the list is fetched once per slug and mode.
   * With it on, only while the cached list (R14) is inside its freshness window
   * and no write has dropped it, so reference data is re-read after five
   * minutes rather than never.
   */
  private cachedListIsCurrent(slug: string, options?: TFetchTeamsOptions): boolean {
    if (!this.queryCacheEnabled) return true;
    const client = peekQueryClient();
    if (!client) return false;
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      return false;
    }
    const query = client
      .getQueryCache()
      .find({ queryKey: teamsBankKey(scope, teamsIncludeOf(options)), exact: true });
    return Boolean(
      query && !query.state.isInvalidated && !query.isStaleByTime(FRESHNESS.reference.staleTime),
    );
  }

  /**
   * The team list for one include mode. With the `query-cache` flag off this is
   * the service call, unchanged. With it on, the read goes through the cached
   * list for that mode (R14, `app/queries/teams.ts`), so the loaders and mounts
   * of one journey share one request, and a mode never answers for another.
   *
   * `fresh` is false when the cache answered with the list this store already
   * projected: putting it into `teamsBySlug` again could undo a roster read or a
   * write applied since.
   */
  private async readTeamList(
    slug: string,
    options?: TFetchTeamsOptions,
  ): Promise<{ teams: TTeam[]; fresh: boolean }> {
    const service = this.rootStore.workspaceTeamService;
    if (!this.queryCacheEnabled) return { teams: await service.list(slug, options), fresh: true };
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      // No signed-in principal yet, so no key to cache under: read the way the
      // flag-off path does.
      console.warn("[workspace-teams] team list read before the session resolved; not cached", {
        slug,
      });
      return { teams: await service.list(slug, options), fresh: true };
    }
    const query = teamsBankQuery(service, scope, teamsIncludeOf(options));
    const client = getQueryClient();
    const dropsAtRequest = this.bankDropCount;
    let teams: TTeam[];
    try {
      teams = await client.fetchQuery(query);
    } catch (error) {
      // A write dropped the entry this read was filling, which cancels it. Its
      // answer would have predated the write: read again, after it.
      if (isCancelledError(error) && this.bankDropCount !== dropsAtRequest) {
        return this.readTeamList(slug, options);
      }
      throw error;
    }
    const projectionKey = JSON.stringify(query.queryKey);
    const answeredAt = client.getQueryState(query.queryKey)?.dataUpdatedAt ?? 0;
    const fresh = this.projectedBankAt.get(projectionKey) !== answeredAt;
    this.projectedBankAt.set(projectionKey, answeredAt);
    return { teams, fresh };
  }

  /**
   * With the `query-cache` flag on, drop the cached team lists a write has made
   * stale, so the next read asks the server rather than undoing the write: every
   * mode after a team write, and the modes that embed rosters after a roster
   * write. Removed rather than patched or invalidated until the writers are
   * converted one by one (contract §3.2); a fresh read is the one answer that
   * cannot be stale. A read in flight is cancelled and reads again.
   */
  private dropCachedTeamLists(slug: string, which: "all" | "rosters"): void {
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    const rosterModes: TeamsInclude[] = ["members", "all+members"];
    this.bankDropCount += 1;
    client.removeQueries({
      // R14 `[slug, uid, 'teams', 'bank', {include}]` and R15
      // `[slug, uid, 'teams', 'page', {…, include}]`, for whichever principal
      // holds them in this workspace.
      predicate: ({ queryKey }) => {
        if (queryKey[0] !== slug || queryKey[2] !== "teams") return false;
        if (queryKey[3] !== "bank" && queryKey[3] !== "page") return false;
        if (which === "all") return true;
        const include = (queryKey[4] as { include?: TeamsInclude } | undefined)?.include;
        return include !== undefined && rosterModes.includes(include);
      },
    });
  }

  /**
   * With the `query-cache` flag on, drop what a roster write stales: the touched
   * teams' rosters (R17) and every member's team list (R19) in the workspace,
   * since the write's member ids are not the ids R19 is keyed by. Both are
   * access-sensitive, so nothing settled is served anyway; this stops a read
   * started after the write from joining one started before it.
   */
  private dropCachedRosters(slug: string, teamIds: readonly string[]): void {
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    const teams = new Set(teamIds);
    this.bankDropCount += 1;
    client.removeQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === slug &&
        queryKey.length === 5 &&
        ((queryKey[2] === "team" &&
          teams.has(queryKey[3] as string) &&
          queryKey[4] === "members") ||
          (queryKey[2] === "member" && queryKey[4] === "teams")),
    });
  }

  /**
   * With the `query-cache` flag on, drop every cached sub-team list (R18) in the
   * workspace after a team write: a create, a move or a delete changes some
   * team's children, and which ones is not worth working out until the writers
   * are converted one by one (contract §3.2).
   */
  private dropCachedChildren(slug: string): void {
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    this.bankDropCount += 1;
    client.removeQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === slug && queryKey[2] === "team" && queryKey[4] === "children",
    });
  }

  /**
   * Read through a cached entry when the `query-cache` flag is on, or call the
   * service when it is off or no principal is signed in yet. A read a write's
   * drop cancelled reads again, after the write.
   */
  private async readThrough<T>(
    slug: string,
    what: string,
    direct: () => Promise<T>,
    cached: (scope: QueryScope) => Promise<T>,
  ): Promise<T> {
    if (!this.queryCacheEnabled) return direct();
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      console.warn(`[workspace-teams] ${what} read before the session resolved; not cached`, {
        slug,
      });
      return direct();
    }
    const dropsAtRequest = this.bankDropCount;
    try {
      return await cached(scope);
    } catch (error) {
      if (isCancelledError(error) && this.bankDropCount !== dropsAtRequest) {
        return this.readThrough(slug, what, direct, cached);
      }
      throw error;
    }
  }

  /**
   * What an update to one team does to the cache (contract §3.2), with the
   * `query-cache` flag on. The team's detail (R16) is dropped. Its roster (R17)
   * is dropped when the leads changed, since a lead's role is on it. A move to
   * another parent drops the lists (R14, R15) and every sub-team list (R18):
   * the tree's shape and depths changed. So does a change to the team's
   * projects, which the lists filter on. Anything else, a rename, a new
   * description, new leads, only changes the team's own row, so the cached
   * lists and pages are patched with the saved row and nothing is re-read.
   */
  private invalidateAfterTeamUpdate(
    slug: string,
    id: string,
    data: TUpdateTeam,
    before: TTeam | undefined,
    updated: TTeam,
  ): void {
    if (!this.queryCacheEnabled) return;
    this.dropCachedTeamDetail(slug, id);
    if (data.lead_ids !== undefined) this.dropCachedRosters(slug, [id]);
    const moved = data.parent !== undefined && (before?.parent ?? null) !== (data.parent ?? null);
    if (moved || data.project_ids !== undefined) {
      this.dropCachedTeamLists(slug, "all");
      if (moved) this.dropCachedChildren(slug);
      return;
    }
    this.patchCachedTeamRows(slug, updated);
  }

  /**
   * Put a saved team's row into every cached list and page set (R14, R15) in
   * `slug` that holds it. A list being read right now is dropped instead: its
   * answer was asked for before the save, and a patch would be undone when it
   * lands, so its reader reads again after the save (`readTeamList`).
   */
  private patchCachedTeamRows(slug: string, updated: TTeam): void {
    const client = peekQueryClient();
    if (!client) return;
    const lists = client.getQueryCache().findAll({
      predicate: ({ queryKey }) =>
        queryKey[0] === slug &&
        queryKey[2] === "teams" &&
        (queryKey[3] === "bank" || queryKey[3] === "page"),
    });
    const withRow = (row: TTeam) => (row.id === updated.id ? { ...row, ...updated } : row);
    for (const query of lists) {
      if (query.state.fetchStatus !== "idle") {
        this.bankDropCount += 1;
        client.removeQueries({ queryKey: query.queryKey, exact: true });
        continue;
      }
      if (query.queryKey[3] === "bank") {
        client.setQueryData<TTeam[]>(query.queryKey, (rows) => rows?.map(withRow));
      } else {
        client.setQueryData<InfiniteData<TTeamsPage>>(query.queryKey, (set) =>
          set
            ? {
                ...set,
                pages: set.pages.map((page) => ({ ...page, results: page.results.map(withRow) })),
              }
            : set,
        );
      }
    }
  }

  /**
   * With the `query-cache` flag on, drop one team's cached detail (R16) after a
   * write to it, for the reason `dropCachedTeamLists` drops the lists. Only the
   * detail: the roster and sub-teams under it are written by other calls.
   */
  private dropCachedTeamDetail(slug: string, teamId: string): void {
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    this.bankDropCount += 1;
    client.removeQueries({
      // R16 `[slug, uid, 'team', id]` exactly, for whichever principal holds it.
      predicate: ({ queryKey }) =>
        queryKey.length === 4 &&
        queryKey[0] === slug &&
        queryKey[2] === "team" &&
        queryKey[3] === teamId,
    });
  }

  /**
   * The first page of the teams list, or the next one, for `query`. With the
   * `query-cache` flag off this is the service call, unchanged. With it on, the
   * pages come from the cached page set for that query (R15), so returning to
   * the list inside its freshness window makes no request, and the next page is
   * fetched into that set. `pages` is every page the set holds; `fresh` is false
   * when it is the set `pagedTeams` was last filled from, so a row this store
   * patched since is not put back.
   */
  private async readTeamsPages(
    slug: string,
    query: TTeamsListQuery,
    cursor: string | null,
  ): Promise<{ pages: TTeamsPage[]; fresh: boolean }> {
    const service = this.rootStore.workspaceTeamService;
    const direct = async () => ({
      pages: [await service.listPage(slug, { ...query, cursor })],
      fresh: true,
    });
    if (!this.queryCacheEnabled) return direct();
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      console.warn("[workspace-teams] teams page read before the session resolved; not cached", {
        slug,
      });
      return direct();
    }
    const options = teamsPageQuery(service, scope, {
      search: query.search,
      order: query.order,
      mine: query.mine,
      rootOnly: query.rootOnly,
      include: teamsIncludeOf(query),
    });
    const client = getQueryClient();
    const dropsAtRequest = this.bankDropCount;
    let pages: TTeamsPage[];
    try {
      if (cursor === null) {
        pages = (await client.fetchInfiniteQuery(options)).pages;
      } else {
        // Fetches the page after the last one the set holds, which is the one
        // `listPageInfo.nextCursor` names.
        const result = await new InfiniteQueryObserver(client, options).fetchNextPage();
        if (result.error) throw result.error;
        pages = result.data?.pages ?? [];
      }
    } catch (error) {
      if (isCancelledError(error) && this.bankDropCount !== dropsAtRequest) {
        return this.readTeamsPages(slug, query, null);
      }
      throw error;
    }
    const key = JSON.stringify(options.queryKey);
    const at = client.getQueryState(options.queryKey)?.dataUpdatedAt ?? 0;
    const fresh = !(this.projectedPage?.key === key && this.projectedPage.at === at);
    this.projectedPage = { key, at };
    return { pages, fresh };
  }

  /**
   * Record which teams a mode's list holds. A list with rosters holds the same
   * teams as the one without, which is what lets a plain read share its request
   * (`fetchTeams`), so it records both.
   */
  private noteListed(slug: string, options: TFetchTeamsOptions | undefined, rows: TTeam[]): void {
    const include = teamsIncludeOf(options);
    const ids: ReadonlySet<string> = new Set(rows.map((team) => team.id));
    this.listedIdsByMode.set(listedKey(slug, include), ids);
    const without = WITHOUT_ROSTERS[include];
    if (without) this.listedIdsByMode.set(listedKey(slug, without), ids);
  }

  private async loadTeams(
    slug: string,
    key: string,
    options?: TFetchTeamsOptions,
  ): Promise<TTeam[]> {
    const readStartedAt = this.writeCount;
    const rosterReadStartedAt = this.rosterWriteCount;
    this.isFetching = true;
    this.loadError = null;
    try {
      const { teams: res, fresh } = await this.readTeamList(slug, options);
      // The cache answered with the list already projected: nothing to apply.
      if (!fresh) return res;
      runInAction(() => {
        const rows = this.withoutStaleRows(slug, res, readStartedAt);
        this.teamsBySlug.set(slug, this.mergeIntoCache(slug, rows, options));
        this.fetchedSlugs.set(key, true);
        if (this.queryCacheEnabled) this.noteListed(slug, options, rows);
        if (options?.includeMembers) {
          // The members ride the list payload (#2016) — cache them where the
          // per-team getMembers/fetchMembers path already looks. Guard on the
          // key so a BE that predates include=members (deploy gap) doesn't
          // clobber an already-fetched cache with empties. A roster this store
          // wrote while the read was in flight keeps the write (NG-2311).
          for (const team of res) {
            if (team.members && !this.rosterWrittenSince(team.id, rosterReadStartedAt)) {
              this.membersByTeamId.set(team.id, team.members);
            }
          }
          this.fetchedSlugs.set(this.fetchKey(slug, undefined), true);
        }
      });
      return res;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      // Drop the shared promise before clearing isFetching so a later refetch
      // (e.g. after a failure) starts a fresh request rather than reusing a
      // settled one.
      this.pendingFetches.delete(key);
      runInAction(() => {
        this.isFetching = false;
      });
    }
  }

  /**
   * Load the FIRST page of the server-driven teams list, replacing whatever is
   * cached. `query` carries the search text / order / include flags; the cursor
   * is owned here.
   *
   * An invalid `order` is a 400 from the API and is deliberately NOT swallowed —
   * it is logged with the failing query and rethrown so the caller (and the
   * table's error state) sees it, rather than the list silently rendering the
   * default ordering.
   */
  async loadTeamsPage(slug: string, query: TTeamsListQuery = {}): Promise<void> {
    this.isListPageLoading = true;
    this.listError = null;
    this.lastListQuery = query;
    try {
      const { pages, fresh } = await this.readTeamsPages(slug, query, null);
      const page = pages[pages.length - 1];
      if (!fresh || !page) return;
      runInAction(() => {
        this.pagedTeams = pages.length === 1 ? page.results : pagesToRows(pages);
        this.listPageInfo = {
          // The FILTERED total — under `search` this is the number of matches,
          // which is what the list's count subtitle must show.
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      console.error("[workspace-teams] loading the teams list page failed", { slug, query, err });
      runInAction(() => {
        this.listError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isListPageLoading = false;
      });
    }
  }

  /**
   * Append the next page (DataTable infinite scroll), reusing the last query so
   * the search/order the user is looking at carries across pages. Falls back to
   * a first-page load when nothing is cached (the error-empty "Try again" path).
   *
   * Because `order=hierarchy` puts a parent ahead of its children, an appended
   * page attaches to parents already in `pagedTeams` — the tree never has to
   * hold the whole workspace to render.
   */
  async loadMoreTeams(slug: string): Promise<void> {
    if (this.pagedTeams.length === 0) {
      await this.loadTeamsPage(slug, this.lastListQuery);
      return;
    }
    if (!this.listPageInfo.hasNext || !this.listPageInfo.nextCursor || this.isListLoadingMore)
      return;
    this.isListLoadingMore = true;
    this.listError = null;
    try {
      const { pages } = await this.readTeamsPages(
        slug,
        this.lastListQuery,
        this.listPageInfo.nextCursor,
      );
      const page = pages[pages.length - 1];
      if (!page) return;
      runInAction(() => {
        const seen = new Set(this.pagedTeams.map((t) => t.id));
        this.pagedTeams = [...this.pagedTeams, ...page.results.filter((t) => !seen.has(t.id))];
        this.listPageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      console.error("[workspace-teams] loading the next teams page failed", {
        slug,
        cursor: this.listPageInfo.nextCursor,
        err,
      });
      runInAction(() => {
        this.listError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isListLoadingMore = false;
      });
    }
  }

  /** Reflect a mutated team in the paged list without refetching the page (the
   *  list is the only consumer of `pagedTeams`, and a refetch would reset the
   *  user's scroll + accumulated pages). No-op when the row isn't loaded. */
  private applyPagedTeamPatch(updated: TTeam): void {
    if (!this.pagedTeams.some((t) => t.id === updated.id)) return;
    this.pagedTeams = this.pagedTeams.map((t) => (t.id === updated.id ? updated : t));
  }

  /**
   * Cache-first prime for ONE team, for consumers that need a single team's row
   * (the breadcrumb's team crumb on the team-scoped project pages) rather than
   * the whole workspace list — the `/teams` tree no longer pulls that list up
   * front (GH-3993). Self-catching + `console.warn`: a name that fails to load
   * only degrades a crumb, so it must not reject into `onunhandledrejection`
   * (mirrors `ensureTeams`).
   */
  async ensureTeam(slug: string, id: string): Promise<void> {
    // Keyed off the slug argument, not the `teams` getter, because a route
    // loader can run before the router store has the new workspace param.
    if ((this.teamsBySlug.get(slug) ?? []).some((t) => t.id === id)) return;
    try {
      await this.fetchTeam(slug, id);
    } catch (err) {
      console.warn(`[workspace-teams] ensureTeam failed for team ${id} in workspace ${slug}`, err);
    }
  }

  /**
   * One team. With the `query-cache` flag off this is the service call,
   * unchanged. With it on, the read goes through the cached detail (R16), so the
   * team page's loader and its views share one request, and re-entering the team
   * inside its freshness window makes none.
   */
  private async readTeam(slug: string, id: string): Promise<TTeam> {
    const service = this.rootStore.workspaceTeamService;
    if (!this.queryCacheEnabled) return service.retrieve(slug, id);
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      console.warn("[workspace-teams] team read before the session resolved; not cached", {
        slug,
        teamId: id,
      });
      return service.retrieve(slug, id);
    }
    const dropsAtRequest = this.bankDropCount;
    try {
      return await getQueryClient().fetchQuery(teamDetailQuery(service, scope, id));
    } catch (error) {
      if (isCancelledError(error) && this.bankDropCount !== dropsAtRequest) {
        return this.readTeam(slug, id);
      }
      throw error;
    }
  }

  async fetchTeam(slug: string, id: string): Promise<TTeam> {
    const readStartedAt = this.writeCount;
    const res = await this.readTeam(slug, id);
    // Written here while the read was in flight: the cache already holds the
    // newer copy, or nothing after a delete, so the read's copy is not kept
    // (NG-2295).
    if (this.writtenSince(id, readStartedAt)) {
      return (this.teamsBySlug.get(slug) ?? []).find((t) => t.id === id) ?? res;
    }
    runInAction(() => {
      const list = this.teamsBySlug.get(slug) ?? [];
      const exists = list.some((t) => t.id === id);
      const next = exists ? list.map((t) => (t.id === id ? res : t)) : [...list, res];
      this.teamsBySlug.set(slug, sortTeams(next));
      this.applyPagedTeamPatch(res);
    });
    return res;
  }

  /** Cached "can the caller create a team?" verdict for a workspace, or
   *  `undefined` until {@link fetchCanCreate} has answered. Callers gate the
   *  "Create team" button on `=== true` so it stays hidden while unanswered
   *  (avoids a flash) and when the caller cannot create. */
  getCanCreate(workspaceSlug: string): boolean | undefined {
    return this.canCreateByWorkspace.get(workspaceSlug);
  }

  /** Probe whether the caller can create a team in this workspace and cache the
   *  verdict. This is the untargeted `team.create` capability check the
   *  can-endpoint answers as "could I create under some team I lead, or at the
   *  workspace scope?" (NG-2176): the button renders before any parent is
   *  picked, so there is no `team_id`. Callers holding `team.create` in the
   *  workspace array already know the answer and skip the probe. */
  async fetchCanCreate(workspaceSlug: string): Promise<boolean> {
    const [result] = await this.rootStore.permissionService.can(workspaceSlug, [
      { action: "team.create" },
    ]);
    const can = result?.can ?? false;
    runInAction(() => {
      this.canCreateByWorkspace.set(workspaceSlug, can);
    });
    return can;
  }

  async createTeam(
    slug: string,
    data: TCreateTeam,
    source: AnalyticsSource = "page",
  ): Promise<TTeam> {
    const created = await this.rootStore.workspaceTeamService.create(slug, data);
    runInAction(() => {
      this.noteWrite(created.id);
      this.dropCachedTeamLists(slug, "all");
      this.dropCachedChildren(slug);
      // Whoever created a team can see it, so every list held for this
      // workspace shows it until the next read replaces the list.
      for (const [listKey, ids] of this.listedIdsByMode) {
        if (listKey.startsWith(`${slug}::`)) {
          this.listedIdsByMode.set(listKey, new Set([...ids, created.id]));
        }
      }
      const next = [...(this.teamsBySlug.get(slug) ?? []), created];
      this.teamsBySlug.set(slug, sortTeams(next));
      // Append to the paged list too so the new team shows without a refetch.
      // Order doesn't matter: the list builds its tree from `parent`, not from
      // array position, and sorts siblings itself.
      if (!this.pagedTeams.some((t) => t.id === created.id)) {
        this.pagedTeams = [...this.pagedTeams, created];
        this.listPageInfo = {
          ...this.listPageInfo,
          totalCount: this.listPageInfo.totalCount + 1,
        };
      }
      if (data.parent) {
        this.childrenByTeamId.delete(childrenCacheKey(data.parent, "direct"));
        this.childrenByTeamId.delete(childrenCacheKey(data.parent, "all"));
      }
    });
    track("team_created", { team_id: created.id, has_parent: Boolean(data.parent), source });
    return created;
  }

  async updateTeam(
    slug: string,
    id: string,
    data: TUpdateTeam,
    source: AnalyticsSource = "page",
  ): Promise<TTeam> {
    // Read before the write: `fields` is what this save changed (NG-2063).
    const before = (this.teamsBySlug.get(slug) ?? []).find((t) => t.id === id);
    const fields = changedFields(data, before);
    const updated = await this.rootStore.workspaceTeamService.update(slug, id, data);
    runInAction(() => {
      this.noteWrite(id);
      this.invalidateAfterTeamUpdate(slug, id, data, before, updated);
      const list = this.teamsBySlug.get(slug) ?? [];
      this.teamsBySlug.set(slug, sortTeams(list.map((t) => (t.id === id ? updated : t))));
      this.applyPagedTeamPatch(updated);
    });
    track("team_updated", { team_id: id, fields, source });
    return updated;
  }

  async updateLeads(slug: string, id: string, leadIds: string[]): Promise<TTeam> {
    const updated = await this.updateTeam(slug, id, { lead_ids: leadIds });
    return updated;
  }

  async updateProjects(slug: string, id: string, projectIds: string[]): Promise<TTeam> {
    return this.updateTeam(slug, id, { project_ids: projectIds });
  }

  async deleteTeam(
    slug: string,
    id: string,
    source: AnalyticsSource = "page",
    options?: TDeleteTeamOptions,
  ): Promise<void> {
    // Without options the call is the bare two-argument one it always was: the
    // flag-off dialog sends nothing new (NG-2157).
    if (options) {
      await this.rootStore.workspaceTeamService.remove(slug, id, options);
    } else {
      await this.rootStore.workspaceTeamService.remove(slug, id);
    }
    track("team_deleted", { team_id: id, source });
    await this.forgetDeletedTeam(slug, id, { promoted: Boolean(options?.promoteSubteams) });
  }

  /**
   * Drop a team the server has deleted from what this store holds. `promoted`
   * says its sub-teams moved to the top level, which stales the cached rows'
   * parent and depth too, so those are reloaded. Public because a team can be
   * deleted by more than `deleteTeam`: the root-team reassignment deletes it on
   * the server once every record has a new Home team (NG-2156).
   */
  async forgetDeletedTeam(
    slug: string,
    id: string,
    { promoted }: { promoted: boolean },
  ): Promise<void> {
    // Read before the row is dropped: a page whose only row was this team is
    // still a page the user is looking at, and its promoted sub-teams belong on it.
    const reload = promoted
      ? { list: this.teamsBySlug.has(slug), page: this.pagedTeams.length > 0 }
      : null;
    runInAction(() => {
      this.noteWrite(id);
      this.dropCachedTeamLists(slug, "all");
      this.dropCachedChildren(slug);
      this.dropCachedTeamDetail(slug, id);
      this.teamsBySlug.set(
        slug,
        (this.teamsBySlug.get(slug) ?? []).filter((t) => t.id !== id),
      );
      if (this.pagedTeams.some((t) => t.id === id)) {
        this.pagedTeams = this.pagedTeams.filter((t) => t.id !== id);
        this.listPageInfo = {
          ...this.listPageInfo,
          totalCount: Math.max(0, this.listPageInfo.totalCount - 1),
        };
      }
      this.membersByTeamId.delete(id);
      this.dropCachedRosters(slug, [id]);
    });
    if (reload) await this.refetchAfterPromotion(slug, reload);
  }

  /**
   * A promoting delete (NG-2157) moved the team's sub-teams to the top level, so
   * the cached rows' parent and depth are stale, not only the deleted team's row.
   * Reload what this store holds for the workspace rather than patching rows it
   * cannot see (a sub-tree's descendants are on later pages). The delete has
   * already succeeded, so a failed reload is logged and the stale list stays.
   */
  private async refetchAfterPromotion(
    slug: string,
    held: { list: boolean; page: boolean },
  ): Promise<void> {
    runInAction(() => {
      this.childrenByTeamId.clear();
      this.dropCachedChildren(slug);
    });
    const reloads: Promise<unknown>[] = [];
    if (held.list) reloads.push(this.fetchTeams(slug));
    if (held.page) reloads.push(this.loadTeamsPage(slug, this.lastListQuery));
    const results = await Promise.allSettled(reloads);
    for (const result of results) {
      if (result.status === "rejected") {
        console.warn("[workspace-teams] reloading teams after a promoting delete failed", {
          slug,
          err: result.reason,
        });
      }
    }
  }

  /** What deleting `id` would touch (NG-2157). Not cached: the delete dialog asks
   *  each time it opens, because the answer is what decides its screen. */
  fetchDeletePreflight(slug: string, id: string): Promise<TTeamDeletePreflight> {
    return this.rootStore.workspaceTeamService.deletePreflight(slug, id);
  }

  async fetchChildren(
    slug: string,
    teamId: string,
    mode: TChildrenMode = "direct",
  ): Promise<TTeam[]> {
    const service = this.rootStore.workspaceTeamService;
    const res = await this.readThrough(
      slug,
      "sub-team",
      () => service.listChildren(slug, teamId, mode),
      (scope) => getQueryClient().fetchQuery(teamChildrenQuery(service, scope, teamId, mode)),
    );
    runInAction(() => {
      this.childrenByTeamId.set(childrenCacheKey(teamId, mode), sortTeams(res));
    });
    return res;
  }

  /** The team's roster. A read answered before this store's own add or remove on
   *  that team leaves the write in place and returns it (NG-2311). */
  async fetchMembers(slug: string, teamId: string): Promise<TTeamMember[]> {
    const readStartedAt = this.rosterWriteCount;
    const service = this.rootStore.workspaceTeamService;
    const res = await this.readThrough(
      slug,
      "team roster",
      () => service.listMembers(slug, teamId),
      (scope) => getQueryClient().fetchQuery(teamMembersQuery(service, scope, teamId)),
    );
    if (this.rosterWrittenSince(teamId, readStartedAt)) {
      // A member-axis write (`changeMemberTeams`) drops the roster rather than
      // writing one, so there is no newer copy to return: read again, after it.
      if (!this.membersByTeamId.has(teamId)) return this.fetchMembers(slug, teamId);
      return this.getMembers(teamId);
    }
    runInAction(() => {
      this.membersByTeamId.set(teamId, res);
    });
    return res;
  }

  /** Admin-only, uncached — the graph is inspected ad hoc per user (spike). */
  async fetchPermissionGraph(slug: string, userId: string): Promise<TTeamPermissionGraph> {
    return this.rootStore.workspaceTeamService.getPermissionGraph(slug, userId);
  }

  async setMembers(
    slug: string,
    teamId: string,
    memberIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TTeamMember[]> {
    // The call sends the whole list, so count what it adds and removes against
    // the members already loaded. A list never loaded counts only the adds.
    const loaded = this.membersByTeamId.get(teamId);
    const res = await this.rootStore.workspaceTeamService.setMembers(slug, teamId, memberIds);
    runInAction(() => {
      this.membersByTeamId.set(teamId, res);
      this.noteRosterWrite(teamId);
      this.dropCachedTeamLists(slug, "rosters");
      this.dropCachedRosters(slug, [teamId]);
    });
    const { added, removed } = listChanges(
      (loaded ?? []).map((member) => member.user),
      memberIds,
    );
    if (added > 0) track("team_member_added", { team_id: teamId, count: added, source });
    if (removed > 0) track("team_member_removed", { team_id: teamId, count: removed, source });
    return res;
  }

  /** The teams one member is on, with their role on each (NG-837). */
  async fetchMemberTeams(slug: string, memberId: string): Promise<TMemberTeam[]> {
    const service = this.rootStore.workspaceTeamService;
    return this.readThrough(
      slug,
      "member's teams",
      () => service.listMemberTeams(slug, memberId),
      (scope) => getQueryClient().fetchQuery(memberTeamsQuery(service, scope, memberId)),
    );
  }

  /**
   * Change one member's role on the named teams, from the member axis
   * (`role: null` leaves a team). Answers with every team they are on after it.
   *
   * Goes through the store rather than the service so the roster cache stays
   * honest: `membersByTeamId` backs the team detail page's Members table, and
   * this write adds or drops that member on exactly the teams it names.
   * Dropping those keys makes the next `fetchMembers` re-read them, which is
   * cheaper and less error-prone than patching rows we do not have (the member
   * axis returns teams, not team-member rows). Each drop is recorded as a roster
   * write, so a roster read already in flight cannot put the pre-write roster
   * back when it lands (NG-2311, NG-2545).
   */
  async changeMemberTeams(
    slug: string,
    memberId: string,
    changes: TMemberTeamChange[],
    source: AnalyticsSource = "page",
  ): Promise<TMemberTeam[]> {
    const res = await this.rootStore.workspaceTeamService.changeMemberTeams(
      slug,
      memberId,
      changes,
    );
    // A person's teams, set from their side: which teams changed, as a count.
    track("workspace_member_updated", {
      member_id: memberId,
      fields: ["teams"],
      count: changes.length,
      source,
    });
    runInAction(() => {
      for (const { team } of changes) {
        this.membersByTeamId.delete(team);
        this.noteRosterWrite(team);
      }
      this.dropCachedTeamLists(slug, "rosters");
      this.dropCachedRosters(
        slug,
        changes.map(({ team }) => team),
      );
    });
    return res;
  }

  async removeMember(
    slug: string,
    teamId: string,
    teamMemberId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workspaceTeamService.removeMember(slug, teamId, teamMemberId);
    track("team_member_removed", { team_id: teamId, count: 1, source });
    runInAction(() => {
      this.membersByTeamId.set(
        teamId,
        (this.membersByTeamId.get(teamId) ?? []).filter((m) => m.id !== teamMemberId),
      );
      this.noteRosterWrite(teamId);
      this.dropCachedTeamLists(slug, "rosters");
      this.dropCachedRosters(slug, [teamId]);
    });
  }
}
